import React from 'react'
import { useTranslation } from 'react-i18next'
import { AllocationModel } from '../allocationsEdit.model'
import {
  FormDialog,
  FormDialogMode,
  DataTableColumn,
  DataType,
  DecimalScale
} from '@globaleris/ui.library'
import { calculatePercentage } from '../../../helpers/numberHelper'
import { useReferredState } from '../../../hooks'

export interface AllocationEditFormProps {
  data: AllocationModel
  open: boolean
  onCancel: () => void
  onSave: (modifiedAllocation: AllocationModel) => void
}

enum EnumFields {
  QTY,
  PERCENTAGE
}

const AllocationEditForm: React.FC<AllocationEditFormProps> = ({
  data,
  open = false,
  onCancel,
  onSave
}) => {
  const [t] = useTranslation(['allocationsEdit'])

  const [fieldSelected, fieldSelectedRef, setFieldSelected] = useReferredState<
    EnumFields | undefined
  >(EnumFields.QTY)
  const [allocation, setAllocation] = React.useState<AllocationModel>(data)

  //#region Form Handlers

  const handleQuantityFormFieldChange = (value: string | number) => {
    if (fieldSelectedRef.current === EnumFields.QTY) {
      setAllocation({
        ...allocation,
        usePercentage: calculatePercentage(+value / allocation?.productQdHired!)
      })
    }
  }

  const handleUsePercentageFormFieldChange = (value: string | number) => {
    if (fieldSelectedRef.current === EnumFields.PERCENTAGE) {
      setAllocation({
        ...allocation,
        quantity: (allocation?.capacity! * +value) / 100
      })
    }
  }

  const handleFormClose = (modifiedAllocation: AllocationModel) => {
    modifiedAllocation === undefined ? onCancel() : onSave(modifiedAllocation)
  }

  //#endregion

  //#region Form definitions

  const columns: DataTableColumn[] = [
    {
      name: 'quantity',
      label: t('allocationsEdit:forms.editAllocation.inputs.quantity'),
      type: DataType.NUMBER,
      required: true,
      readOnly: false,
      onChange: handleQuantityFormFieldChange,
      onFocus: () => {
        fieldSelectedRef.current = EnumFields.QTY
      },
      decimalScale: DecimalScale.ENERGY
    },
    {
      name: 'usePercentage',
      label: t('allocationsEdit:forms.editAllocation.inputs.percentage'),
      type: DataType.NUMBER,
      required: true,
      readOnly: false,
      onChange: handleUsePercentageFormFieldChange,
      onFocus: () => {
        fieldSelectedRef.current = EnumFields.PERCENTAGE
      },
      decimalScale: DecimalScale.DEFAULT
    }
  ]

  //#endregion

  return (
    <>
      {open ? (
        <FormDialog
          title={allocation?.productCapacityBreakdown}
          inputs={columns}
          inputData={allocation}
          mode={FormDialogMode.EDIT}
          onClose={handleFormClose}
          open={open}
          okBtnLabel={t('allocationsEdit:forms.editAllocation.actions.ok')}
          cancelBtnLabel={t('allocationsEdit:forms.editAllocation.actions.cancel')}
        />
      ) : null}
    </>
  )
}

export default AllocationEditForm
